import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Paginator from '../Paginator';
import {
    parseFilter,
    parseIncludeDeactivated,
    parsePaging,
    parseSorting,
} from '../../utils/queryString';
import { ALL_PRODUCTS, ON_PRODUCT_CHANGED } from '../../graphql/products';
import ProductsList from './ProductsList';
import CreateProductRow from './CreateProductRow';
import { showInfoNotification } from '../../actions/notifications';
import Spinner from '../Spinner';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import SubscriptionActions from '../../utils/SubscriptionActions';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import RadioField from '../fields/RadioField';
import './_styles_/Products.css';
import Error from '../errors/Error';
import Constants from '../../utils/Constants';
import { getValueMapping } from './ProductRow';
import CheckboxField from '../fields/CheckboxField';
import useAuthentication from '../../hooks/useAuthentication';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';

export function createNotificationText(t, action, name, productId) {
    let notification;
    switch (action) {
        case SubscriptionActions.PRODUCT_CREATED:
            notification = t('products.notification.product-create-thirdparty', { name, productId });
            break;
        case SubscriptionActions.PRODUCT_UPDATED:
            notification = t('products.notification.product-update-thirdparty', { name, productId });
            break;
        default:
            notification = t('products.notification.products-modified-thirdparty');
    }
    return notification;
}

function Products() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'productId',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const includeDeactivated = parseIncludeDeactivated(location.search);
    const dispatch = useDispatch();
    const filterOptions = [{
        id: 'ALL',
        label: t('products.filter.all'),
    }, {
        id: 'SECURITY',
        label: t('products.filter.security'),
    }, {
        id: 'ENERGY',
        label: t('products.filter.energy'),
    }];
    const productTypeFilter = parseFilter(location.search);
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        includeDeactivated,
        sortKey,
        sortDirection,
        productTypeFilter,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_PRODUCTS, {
        variables: refetchProps,
        valueMappingFn: getValueMapping,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onIncludeDeactivated = (newIncludeDeactivated) => setRefetchProps({
        ...refetchProps,
        includeDeactivated: newIncludeDeactivated,
        page: 0,
    });
    const onTypeFilter = (newTypeFilter) => setRefetchProps({
        ...refetchProps,
        productTypeFilter: newTypeFilter,
        page: 0,
    });
    useExtendedSubscription(ON_PRODUCT_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'productChanged',
        onChangeCallback: ({ action, model: { name, productId } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.PRODUCT_EDIT])) {
                const notification = createNotificationText(t, action, name, productId);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result = null;
    let productTypesFilter = null;
    let productProps;
    if (loading) {
        result = <Spinner className="products-loading" />;
    } else if (error) {
        result = <Error className="products-error" error={error} />;
    } else {
        productProps = {
            availableDeliveryTypes: data.allProducts.availableDeliveryTypes,
            availableDeliveryPeriods: data.allProducts.availableDeliveryPeriods,
            availableQuantityUnits: data.allProducts.availableQuantityUnits,
            availableProductTypes: data.allProducts.availableProductTypes,
        };
        let activeProductType = productProps.availableProductTypes[0];
        if (productProps.availableProductTypes.length > 1) {
            productTypesFilter = (
                <RadioField
                    values={filterOptions}
                    selectedValue={refetchProps.productTypeFilter}
                    onChange={onTypeFilter}
                    className="products--search-and-filter--filter-products"
                    inputIdBase="products--search-and-filter--filter-products"
                />
            );
            activeProductType = refetchProps.productTypeFilter;
        }
        if (data.allProducts.productList.totalCount) {
            result = (
                <Paginator
                    totalCount={data.allProducts.productList.totalCount}
                    perPage={perPage}
                    currentPage={refetchProps.page}
                    onPageChange={onPageChange}
                    bottom
                >
                    <ProductsList
                        products={data.allProducts.productList.models}
                        productProps={productProps}
                        activeProductType={activeProductType}
                        onSort={onSort}
                        sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                    />
                </Paginator>
            );
        }
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="boxes" />&nbsp;{t('title.products')}</h4>
            {productProps && <CreateProductRow productProps={productProps} />}
            <div className="products search-and-filter">
                <div className="filter">
                    {productTypesFilter}
                    <CheckboxField
                        label={t('products.include-deactivated')}
                        value={refetchProps.includeDeactivated}
                        onChange={onIncludeDeactivated}
                        className="products--search-and-filter--include-deactivated"
                        inputId="products--search-and-filter--include-deactivated"
                    />
                </div>
            </div>
            {result}
        </>
    );
}

export default Products;
